import React, { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import ChessBoardView from '@/components/chess/ChessBoardView';
import ExplanationViewer from '@/components/studies/ExplanationViewer';
import {
  ChevronRight, ChevronDown, Folder, Globe, Users, Lock, Search,
  FileText, Database as DbIcon, GraduationCap, Sword, Send, Play, Loader2, PanelRightClose, Video, BookOpen
} from 'lucide-react';

const visIcon = (v) => v === 'public' ? <Globe className="w-3.5 h-3.5"/> : v === 'shared' ? <Users className="w-3.5 h-3.5"/> : <Lock className="w-3.5 h-3.5"/>;
const ACT_LABEL = { capture:'Capture', puzzle:'Puzzle/Tactics', multiple_choice:'Multiple Choice', explanation:'Explanation', play_computer:'Play with Computer', video:'Video' };

function Group({ label, count, open, onToggle, children, testId }) {
  return (
    <div className="mb-1" data-testid={testId}>
      <button onClick={onToggle} className="w-full flex items-center gap-2 px-2 py-1.5 hover:bg-slate-100 rounded text-sm font-semibold">
        {open ? <ChevronDown className="w-3.5 h-3.5"/> : <ChevronRight className="w-3.5 h-3.5"/>}
        <Folder className="w-4 h-4 text-slate-500"/>{label}
        <span className="ml-auto text-xs text-slate-400 font-mono">{count}</span>
      </button>
      {open && <div className="ml-3 mt-1 space-y-0.5">{children}</div>}
    </div>
  );
}

function ClassroomMaterialInner({ roomId, isTeacher, onLoadGame, onLaunch, onHide, onStartExplanation }) {
  const [dbs, setDbs] = useState({ mine: [], shared: [], public: [] });
  const [studies, setStudies] = useState({ mine: [], shared: [], public: [], assigned: [] });
  const [folders, setFolders] = useState([]);
  const [openGroups, setOpenGroups] = useState({ mine: true, shared: false, public: false });
  const [openFolders, setOpenFolders] = useState({}); // { folder_id | '__none__': bool }
  const [expandedDb, setExpandedDb] = useState(null);
  const [dbGames, setDbGames] = useState([]);
  const [loadingGames, setLoadingGames] = useState(false);
  const [expandedStudy, setExpandedStudy] = useState(null);
  const [studyDetail, setStudyDetail] = useState(null);
  const [explanationViewer, setExplanationViewer] = useState(null); // Explanation V2 · Etapa 1
  const [master, setMaster] = useState([]);
  const [q, setQ] = useState('');

  const loadAll = useCallback(async () => {
    try {
      const [d, s, f] = await Promise.all([
        api.get('/pgn-databases'),
        api.get('/studies'),
        api.get('/study-folders'),
      ]);
      setDbs(d.data);
      setStudies({
        mine:     s.data.mine || [],
        shared:   s.data.shared || [],
        public:   s.data.public || [],
        assigned: s.data.assigned || [],
      });
      setFolders(f.data.folders || []);
    } catch { /* silent */ }
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  const toggleDb = async (db) => {
    if (expandedDb === db.id) { setExpandedDb(null); setDbGames([]); return; }
    setExpandedDb(db.id); setLoadingGames(true); setDbGames([]);
    try {
      const { data } = await api.get(`/pgn-databases/${db.id}/games`, { params: { q: q || undefined, limit: 100 } });
      setDbGames(data.items || []);
    } catch { setDbGames([]); }
    finally { setLoadingGames(false); }
  };

  const toggleStudy = async (st) => {
    if (expandedStudy === st.id) { setExpandedStudy(null); setStudyDetail(null); return; }
    setExpandedStudy(st.id); setStudyDetail(null);
    try { const { data } = await api.get(`/studies/${st.id}`); setStudyDetail(data); }
    catch { toast.error('No se pudo abrir el estudio'); }
  };

  const loadMaster = useCallback(async () => {
    try { const { data } = await api.get('/games'); setMaster(data || []); } catch { setMaster([]); }
  }, []);

  const filterName = (arr, key='name') => !q ? arr : arr.filter(x => (x[key]||'').toLowerCase().includes(q.toLowerCase()));

  const DbList = ({ items, groupKey }) => (
    filterName(items).length === 0 ? <div className="text-xs text-slate-400 px-2 py-1">Vacío</div> :
    filterName(items).map(db => (
      <div key={db.id}>
        <button data-testid={`mat-db-${db.id}`} onClick={()=>toggleDb(db)}
          className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm ${expandedDb===db.id?'bg-indigo-50 text-indigo-700':'hover:bg-slate-100'}`}>
          {expandedDb===db.id ? <ChevronDown className="w-3 h-3"/> : <ChevronRight className="w-3 h-3"/>}
          {visIcon(db.visibility)}
          <span className="flex-1 truncate text-left">{db.name}</span>
          <span className="text-[10px] text-slate-400 font-mono">{db.games_count ?? 0}</span>
        </button>
        {expandedDb===db.id && (
          <div className="ml-5 mt-0.5 space-y-0.5">
            {loadingGames ? <div className="text-xs text-slate-400 flex items-center gap-1 px-2 py-1"><Loader2 className="w-3 h-3 animate-spin"/> Cargando…</div> :
             dbGames.length === 0 ? <div className="text-xs text-slate-400 px-2 py-1">Sin partidas</div> :
             dbGames.map(g => (
              <button key={g.id} data-testid={`mat-game-${g.id}`} onClick={()=>onLoadGame?.(g)}
                className="w-full text-left flex items-center gap-2 px-2 py-1 rounded text-xs hover:bg-slate-100">
                <Sword className="w-3 h-3 text-slate-400 shrink-0"/>
                <span className="truncate">{g.white_player} <span className="text-slate-400">vs</span> {g.black_player}</span>
                <span className="ml-auto text-[10px] text-slate-400 font-mono">{g.result}</span>
              </button>
             ))}
          </div>
        )}
      </div>
    ))
  );

  const StudyList = ({ items }) => (
    filterName(items).length === 0 ? <div className="text-xs text-slate-400 px-2 py-1">Vacío</div> :
    filterName(items).map(st => (
      <div key={st.id}>
        <button data-testid={`mat-study-${st.id}`} onClick={()=>toggleStudy(st)}
          className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm ${expandedStudy===st.id?'bg-indigo-50 text-indigo-700':'hover:bg-slate-100'}`}>
          {expandedStudy===st.id ? <ChevronDown className="w-3 h-3"/> : <ChevronRight className="w-3 h-3"/>}
          <GraduationCap className="w-3.5 h-3.5"/>
          <span className="flex-1 truncate text-left">{st.name}</span>
          <span className="text-[10px] text-slate-400 font-mono">{st.activities_count ?? 0}</span>
        </button>
        {expandedStudy===st.id && (
          <div className="ml-5 mt-0.5 space-y-1">
            {!studyDetail ? <div className="text-xs text-slate-400 px-2 py-1">Cargando…</div> :
             (studyDetail.activities||[]).length === 0 ? <div className="text-xs text-slate-400 px-2 py-1">Sin actividades</div> :
             (studyDetail.activities||[]).map((ch, idx) => (
              <div key={ch.id} data-testid={`mat-activity-${ch.id}`} className="rounded-xl border border-slate-200 bg-slate-50 p-2">
                <div className="flex gap-2">
                  {ch.type !== 'video' && ch.fen
                    ? <div className="w-20 shrink-0"><ChessBoardView fen={ch.fen} boardWidth={80} arePiecesDraggable={false}/></div>
                    : <div className="w-20 h-20 shrink-0 rounded bg-slate-900 grid place-items-center text-white"><Video className="w-6 h-6"/></div>}
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] text-slate-500">{ACT_LABEL[ch.type] || ch.type}</div>
                    <div className="text-sm font-bold leading-tight truncate" data-testid={`mat-activity-order-${ch.id}`}>Ejercicio {idx + 1}</div>
                    {ch.title && ch.title.trim() && ch.title.trim() !== '?' && (
                      <div className="text-[11px] text-slate-500 leading-tight truncate" title={ch.title}>{ch.title}</div>
                    )}
                    <div className="text-[10px] text-slate-500 mt-1 leading-tight">
                      <div>Score: {ch.points}</div>
                      <div>Max attempts: {ch.max_attempts ?? 3}</div>
                      {ch.type === 'play_computer' && <><div>Goal: {ch.goal === 'draw' ? 'Draw' : 'Win'}</div><div>Student Plays: {ch.student_side === 'black' ? 'Black' : 'White'}</div></>}
                    </div>
                  </div>
                </div>
                {isTeacher && (
                  <div className="grid grid-cols-2 gap-1 mt-2">
                    {ch.type === 'lava_floor' ? (
                      <Button data-testid={`mat-start-lava-explanation-${ch.id}`} size="sm" className="h-7 text-[11px] bg-amber-500 hover:bg-amber-600"
                        onClick={()=>onStartExplanation?.(expandedStudy, ch.id)}>
                        <Play className="w-3 h-3 mr-1"/> Iniciar explicación
                      </Button>
                    ) : ch.type === 'explanation' ? (
                      <>
                        <Button data-testid={`mat-start-explanation-${ch.id}`} size="sm" className="h-7 text-[11px] bg-amber-500 hover:bg-amber-600"
                          onClick={()=>onStartExplanation?.(expandedStudy, ch.id)}>
                          <Play className="w-3 h-3 mr-1"/> Iniciar clase
                        </Button>
                        <Button data-testid={`mat-view-explanation-${ch.id}`} size="sm" variant="outline" className="h-7 text-[11px]"
                          onClick={()=>setExplanationViewer(ch)}>
                          <BookOpen className="w-3 h-3 mr-1"/> Ver pasos
                        </Button>
                      </>
                    ) : ch.type === 'play_computer' && ch.fen ? (
                      <Button data-testid={`mat-ask-${ch.id}`} size="sm" className="h-7 text-[11px] bg-indigo-600 hover:bg-indigo-700"
                        onClick={()=>onLaunch?.({
                          type: 'play_computer',
                          study_id: expandedStudy,
                          activity_id: ch.id,
                          title: ch.title || `Play with Computer ${idx + 1}`,
                          fen: ch.fen,
                          goal: ch.goal || 'win',
                          student_side: ch.student_side || 'white',
                          engine_level: Number.isFinite(ch.engine_level) ? ch.engine_level : 5,
                          points: ch.points ?? 30,
                          max_attempts: ch.max_attempts ?? 3,
                          duration_seconds: ch.time_seconds || 60,
                          instructions: ch.instructions || '',
                        })}>
                        <Send className="w-3 h-3 mr-1"/> Ask
                      </Button>
                    ) : (ch.solution_moves||[]).length > 0 || ch.capture_config ? (
                      <Button data-testid={`mat-ask-${ch.id}`} size="sm" className="h-7 text-[11px] bg-indigo-600 hover:bg-indigo-700"
                        onClick={()=>onLaunch?.({ title: `Ejercicio ${idx + 1}`, fen: ch.fen, solution_moves: ch.solution_moves||[], accepted_solutions: ch.accepted_solutions || null, capture_config: ch.capture_config || null, instructions: ch.instructions || '', points: ch.points, duration_seconds: ch.time_seconds || 60, max_attempts: ch.max_attempts || 3 })}>
                        <Send className="w-3 h-3 mr-1"/> Ask
                      </Button>
                    ) : (
                      <Button data-testid={`mat-ask-disabled-${ch.id}`} size="sm" variant="ghost" disabled className="h-7 text-[11px] text-slate-500">Sin respuesta</Button>
                    )}
                    <Button data-testid={`mat-loadfen-${ch.id}`} size="sm" variant="outline" className="h-7 text-[11px]" disabled={!ch.fen}
                      onClick={()=>onLoadGame?.({ id: null, fen: ch.fen, capture_config: ch.capture_config || null, solution_moves: ch.solution_moves || null, pgn: ch.pgn || null })}>
                      <Play className="w-3 h-3 mr-1"/> Load Game
                    </Button>
                  </div>
                )}
              </div>
             ))}
          </div>
        )}
      </div>
    ))
  );

  return (
    <aside className="bg-white flex flex-col min-h-0 h-full" data-testid="classroom-material">
      <div className="flex items-center justify-between px-3 pt-2">
        <button onClick={onHide} data-testid="material-hide" className="text-sm text-indigo-600 font-semibold flex items-center gap-1 hover:underline">
          <PanelRightClose className="w-4 h-4"/> Ocultar
        </button>
      </div>
      <Tabs defaultValue="studies" className="flex-1 flex flex-col min-h-0"
        onValueChange={(v)=>{ if (v==='masterbase' && master.length===0) loadMaster(); }}>
        <TabsList className="justify-start rounded-none border-b bg-white p-1 mx-2 mt-1 flex-wrap h-auto">
          <TabsTrigger data-testid="mat-tab-studies" value="studies"><GraduationCap className="w-3.5 h-3.5 mr-1"/> Studies</TabsTrigger>
          <TabsTrigger data-testid="mat-tab-masterbase" value="masterbase"><Sword className="w-3.5 h-3.5 mr-1"/> Masterbase</TabsTrigger>
          <TabsTrigger data-testid="mat-tab-pdf" value="pdf"><FileText className="w-3.5 h-3.5 mr-1"/> PDF</TabsTrigger>
          <TabsTrigger data-testid="mat-tab-references" value="references"><DbIcon className="w-3.5 h-3.5 mr-1"/> References</TabsTrigger>
        </TabsList>

        <div className="px-3 pt-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
            <Input data-testid="mat-search" placeholder="Search…" value={q} onChange={(e)=>setQ(e.target.value)} className="pl-9 rounded-lg h-9"/>
          </div>
        </div>

        <TabsContent value="references" className="flex-1 min-h-0 p-3 overflow-y-auto">
          <Group label="My Databases" count={dbs.mine.length} open={openGroups.mine} onToggle={()=>setOpenGroups(s=>({...s,mine:!s.mine}))} testId="mat-mine">
            <DbList items={dbs.mine}/>
          </Group>
          <Group label="Shared Databases" count={dbs.shared.length} open={openGroups.shared} onToggle={()=>setOpenGroups(s=>({...s,shared:!s.shared}))} testId="mat-shared">
            <DbList items={dbs.shared}/>
          </Group>
          <Group label="Public Databases" count={dbs.public.length} open={openGroups.public} onToggle={()=>setOpenGroups(s=>({...s,public:!s.public}))} testId="mat-public">
            <DbList items={dbs.public}/>
          </Group>
        </TabsContent>

        <TabsContent value="pdf" className="flex-1 min-h-0 p-3 overflow-y-auto">
          <div className="grid place-items-center h-full text-center text-sm text-slate-500 py-12">
            <div>
              <FileText className="w-8 h-8 mx-auto mb-2 text-slate-300"/>
              Material PDF por módulo
              <div className="text-xs text-slate-400 mt-1">Próximamente — subiremos PDFs de apoyo por módulo.</div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="masterbase" className="flex-1 min-h-0 p-3 overflow-y-auto">
          {filterName(master, 'white_player').length === 0 ? (
            <div className="text-sm text-slate-500 text-center py-12">Sin partidas maestras.</div>
          ) : filterName(master, 'white_player').map(g => (
            <button key={g.id} data-testid={`mat-master-${g.id}`} onClick={()=>onLoadGame?.(g)}
              className="w-full text-left flex items-center gap-2 px-2 py-1.5 rounded text-xs hover:bg-slate-100 border-b border-slate-50">
              <Sword className="w-3 h-3 text-slate-400 shrink-0"/>
              <span className="truncate">{g.white_player} <span className="text-slate-400">vs</span> {g.black_player}</span>
              <span className="ml-auto text-[10px] text-slate-400 font-mono">{g.result || '*'}</span>
            </button>
          ))}
        </TabsContent>

        <TabsContent value="studies" className="flex-1 min-h-0 p-3 overflow-y-auto" data-testid="mat-studies-tab-content">
          {(() => {
            // Group studies by folder using the SAME data the Studies page uses:
            // /study-folders (owned + assigned to me) + /studies (mine + assigned + shared + public).
            // Backend already filters by role/assignment, so we just render.
            const NO_FOLDER = '__none__';
            const allStudies = [];
            const seen = new Set();
            for (const s of [...studies.mine, ...studies.assigned, ...studies.shared, ...studies.public]) {
              if (s && s.id && !seen.has(s.id)) { seen.add(s.id); allStudies.push(s); }
            }
            const byFolder = new Map();
            for (const s of allStudies) {
              const k = s.folder_id || NO_FOLDER;
              if (!byFolder.has(k)) byFolder.set(k, []);
              byFolder.get(k).push(s);
            }
            // Mantener en el aula el mismo orden personalizado que en la vista del propietario.
            for (const items of byFolder.values()) {
              items.sort((a, b) => {
                const moduleCompare = (a.module || '').localeCompare(b.module || '', 'es');
                if (moduleCompare) return moduleCompare;
                const ao = Number.isFinite(a.study_order) ? a.study_order : Number.MAX_SAFE_INTEGER;
                const bo = Number.isFinite(b.study_order) ? b.study_order : Number.MAX_SAFE_INTEGER;
                if (ao !== bo) return ao - bo;
                return (b.created_at || '').localeCompare(a.created_at || '');
              });
            }
            // Render one Group per known folder (already scoped to the current
            // user server-side). Then "Sin carpeta" only if it has any studies.
            const rendered = [];
            for (const f of folders) {
              const items = byFolder.get(f.id) || [];
              const key = f.id;
              const label = f.name + (f.owned === false ? ' · Asignado' : '');
              rendered.push(
                <Group key={key} label={label} count={items.length}
                  open={openFolders[key] !== false} onToggle={()=>setOpenFolders(s=>({...s,[key]:!(s[key] !== false)}))}
                  testId={`mat-folder-${key}`}>
                  <StudyList items={items}/>
                </Group>
              );
            }
            const unfoldered = byFolder.get(NO_FOLDER) || [];
            if (unfoldered.length > 0) {
              rendered.push(
                <Group key="unfoldered" label="Sin carpeta" count={unfoldered.length}
                  open={openFolders[NO_FOLDER] !== false}
                  onToggle={()=>setOpenFolders(s=>({...s,[NO_FOLDER]:!(s[NO_FOLDER] !== false)}))}
                  testId="mat-folder-unfoldered">
                  <StudyList items={unfoldered}/>
                </Group>
              );
            }
            if (rendered.length === 0) {
              return (
                <div data-testid="mat-studies-empty" className="text-sm text-slate-500 text-center py-12">
                  <GraduationCap className="w-8 h-8 mx-auto mb-2 text-slate-300"/>
                  Aún no tienes Studies asignados.
                </div>
              );
            }
            return rendered;
          })()}
        </TabsContent>
      </Tabs>
      {/* Explanation V2 · Etapa 1: viewer local (NO proyecta al tablero compartido — eso es Etapa 2) */}
      <ExplanationViewer
        open={!!explanationViewer}
        activity={explanationViewer}
        onClose={()=>setExplanationViewer(null)}
      />
    </aside>
  );
}

// Memoize the sidebar so that state changes on ClassroomLive (e.g., polling
// updates to room.fen after a move) don't trigger re-renders of the studies
// list and its many small ChessBoardView thumbnails. Callbacks passed from
// the parent are declared once per render — memo helps only when they are
// stable, but even so it avoids re-rendering when only unrelated props like
// room.fen change on the parent.
const ClassroomMaterial = React.memo(ClassroomMaterialInner);
export default ClassroomMaterial;
